import React, { useEffect, useRef, useState } from 'react';
import { Phone, Mail, MapPin, Clock, Calendar, MessageCircle, Crosshair, Target, Check } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { mockData } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import OccupancyCalendar from '../components/OccupancyCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Remove trailing slash if exists to prevent double slashes
const API = `${BACKEND_URL.replace(/\/$/, '')}/api`;

// Must stay in sync with backend/admin_storage.py -> PACKAGE_TO_PRICE_KEY
const PRICE_KEY_MAP = {
  lasertag: {
    'Pachet Simplu': 'simplu',
    'Pachet Full': 'full',
    'Pachet Standard': 'full',
    'Evenimente Private': 'evenimente',
    'Evenimente Speciale': 'evenimente',
  },
  paintball: {
    'Pachet Paintball': 'pachet',
    'Pachet Standard': 'pachet',
    'Refill': 'refill',
    'Evenimente Private': 'evenimente',
    'Evenimente Speciale': 'evenimente',
  },
};

const Reservations = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preSelectedPackage = searchParams.get('pachet') || '';
  const preSelectedGameType = searchParams.get('tip') || '';
  
  const [gameType, setGameType] = useState(preSelectedGameType);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    people: '',
    package: preSelectedPackage,
    message: ''
  });
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [livePrices, setLivePrices] = useState(null);
  const [submittingReservation, setSubmittingReservation] = useState(false);
  const [reservationResult, setReservationResult] = useState(null);
  const { toast } = useToast();
  const formRef = useRef(null);

  const handleCalendarDateSelect = (dateStr, gameTypeFromCalendar) => {
    setFormData(prev => ({ ...prev, date: dateStr, time: '' }));
    if (gameTypeFromCalendar && gameTypeFromCalendar !== gameType) {
      setGameType(gameTypeFromCalendar);
    }
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const basePricing = gameType === 'lasertag' ? mockData.pricingLasertag : gameType === 'paintball' ? mockData.pricingPaintball : [];
  const currentPricing = basePricing
    .filter(pkg => pkg.name !== 'Refill' && (gameType !== 'lasertag' || pkg.id !== 2))
    .map(pkg => {
      const key = PRICE_KEY_MAP[gameType]?.[pkg.name];
      const livePrice = key && livePrices?.[gameType]?.[key];
      return livePrice ? { ...pkg, price: livePrice } : pkg;
    });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch live prices once on mount so the package dropdown reflects admin changes
  useEffect(() => {
    axios.get(`${API}/prices`)
      .then(r => setLivePrices(r.data?.data || null))
      .catch(() => setLivePrices(null));
  }, []);

  // Fetch available times when date changes
  useEffect(() => {
    if (!formData.date || !gameType) {
      setAvailableTimes([]);
      return;
    }

    const fetchAvailableTimes = async () => {
      setLoadingTimes(true);
      try {
        const response = await axios.get(`${API}/reservations/available-times?date=${formData.date}&game_type=${gameType}`);
        if (response.data.success) {
          setAvailableTimes(response.data.all_slots || []);
        }
      } catch (error) {
        console.error('Error fetching available times:', error);
        toast({
          title: "Eroare",
          description: "Nu s-au putut încărca orele disponibile.",
          variant: "destructive"
        });
      } finally {
        setLoadingTimes(false);
      }
    };

    fetchAvailableTimes();
  }, [formData.date, gameType, toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert date format from yyyy-mm-dd to dd/mm/yyyy for display
    if (name === 'date') {
      // Keep it in yyyy-mm-dd format for API calls
      setFormData({
        ...formData,
        [name]: value
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Format date for display (dd/mm/yyyy)
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Parse date from dd/mm/yyyy to yyyy-mm-dd
  const parseDateFromDisplay = (displayStr) => {
    if (!displayStr) return '';
    const [day, month, year] = displayStr.split('/');
    return `${year}-${month}-${day}`;
  };

  const validateForm = () => {
    if (!gameType) {
      toast({
        title: "Eroare",
        description: "Te rugăm să selectezi tipul de activitate (Lasertag sau Paintball).",
        variant: "destructive"
      });
      return false;
    }
    if (!formData.name || !formData.email || !formData.phone || !formData.date || !formData.time || !formData.people) {
      toast({
        title: "Eroare",
        description: "Te rugăm să completezi toate câmpurile obligatorii.",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  const submitReservation = async (submissionMethod) => {
    if (!validateForm()) return;
    setSubmittingReservation(true);
    try {
      const response = await axios.post(`${API}/reservations/request`, {
        client_name: formData.name,
        client_phone: formData.phone,
        client_email: formData.email,
        people: formData.people,
        package: formData.package,
        game_type: gameType,
        date: formData.date,
        time: formData.time,
        message: formData.message,
        submission_method: submissionMethod || 'whatsapp',
      });
      setReservationResult({ notificationStatus: response.data?.notification_status || 'failed' });
    } catch (e) {
      toast({
        title: "Nu s-a putut trimite cererea",
        description: e?.response?.data?.detail || "Verifică conexiunea și încearcă din nou.",
        variant: "destructive"
      });
    } finally {
      setSubmittingReservation(false);
    }
  };

  return (
    <div className="reservations-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <Calendar className="page-icon" style={{ color: '#FFFFFF' }} />
          <h1 className="page-title">REZERVĂRI</h1>
          <p className="page-subtitle">Alege-ți tabăra și rezervă acum!</p>
          
          
          <p className="payment-warning">
            ⚠️ Rezervările se vor confirma doar după achitarea avansului de 50% din totalul rezervării
          </p>
        </div>
      </section>

      {/* Occupancy Calendar overview */}
      <section style={{ padding: '32px 0 8px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <h2 style={{ fontSize: '1.6rem', color: '#fff', marginBottom: 6 }}>Disponibilitate la o privire</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', margin: 0 }}>
              Click pe o zi pentru a o selecta în formular. Zilele plin colorate sunt deja ocupate.
            </p>
          </div>
          <OccupancyCalendar
            externalGameType={gameType || 'lasertag'}
            onGameTypeChange={(gt) => {
              setGameType(gt);
              setFormData(prev => ({ ...prev, package: '', time: '' }));
            }}
            selectedDate={formData.date}
            onDateSelect={handleCalendarDateSelect}
          />
        </div>
      </section>

      {/* Reservation Form */}
      <section className="reservation-form-section" ref={formRef}>
        <div className="container">
          <div className="reservation-grid">
            {/* Form */}
            <Card className="reservation-form-card">
              <CardHeader>
                <CardTitle className="form-card-title">Formular de rezervare</CardTitle>
                <p className="form-card-description">Trimite cererea direct și alege metoda preferată pentru a te contacta.</p>
              </CardHeader>
              <CardContent>
                {reservationResult ? (
                  <div role="status" aria-live="polite" style={{ textAlign: 'center', padding: '24px 8px' }}>
                    <div style={{
                      width: 112,
                      height: 112,
                      margin: '0 auto 24px',
                      border: '6px solid #22c55e',
                      borderRadius: '50%',
                      color: '#22c55e',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Check size={64} strokeWidth={3} aria-hidden="true" />
                    </div>
                    <h2 style={{ color: '#22c55e', fontSize: '1.6rem', marginBottom: 12 }}>
                      Cererea ta de rezervare a fost trimisă!
                    </h2>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      Te rugăm să aștepți un mesaj de confirmare de la Combat Zone.
                    </p>
                    {reservationResult.notificationStatus !== 'sent' && (
                      <p style={{ color: '#fbbf24', lineHeight: 1.6 }} role="alert">
                        Cererea a fost înregistrată, dar notificarea automată către Combat Zone
                        {reservationResult.notificationStatus === 'not_configured' ? ' nu este configurată încă.' : ' nu a putut fi trimisă.'}
                        {' '}Te rugăm să ne contactezi la {mockData.contact.phone} sau {mockData.contact.email}.
                      </p>
                    )}
                    <Button
                      type="button"
                      onClick={() => setReservationResult(null)}
                      className="btn-secondary"
                      style={{ marginTop: 20 }}
                    >
                      Trimite o altă cerere
                    </Button>
                  </div>
                ) : (
                <form className="reservation-form">
                  <div className="form-group">
                    <label htmlFor="gameType" className="form-label">Tip activitate *</label>
                    <select
                      id="gameType"
                      name="gameType"
                      value={gameType}
                      onChange={(e) => {
                        setGameType(e.target.value);
                        setFormData({ ...formData, package: '' });
                      }}
                      required
                      className="form-input"
                      style={{ cursor: 'pointer' }}
                    >
                      <option value="">Selectează tipul de activitate...</option>
                      <option value="lasertag">Lasertag</option>
                      <option value="paintball">Paintball</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="package" className="form-label">Pachet dorit *</label>
                    <select
                      id="package"
                      name="package"
                      value={formData.package}
                      onChange={handleChange}
                      required
                      className="form-input"
                      style={{ cursor: 'pointer' }}
                    >
                      <option value="">Selectează un pachet...</option>
                      {currentPricing.map((pkg) => (
                        <option key={pkg.id} value={pkg.name}>
                          {pkg.name === 'Pachet Standard' ? pkg.name : `${pkg.name} - ${pkg.price}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Nume complet *</label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Introdu numele și prenumele tău"
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email *</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="exemplu@email.ro"
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Telefon *</label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+40 XXX XXX XXX"
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
  <label htmlFor="date" className="form-label">Data dorită *</label>
  <div 
    className="date-input-wrapper"
    onClick={() => document.getElementById('date')?.showPicker?.()}
    style={{ position: 'relative', cursor: 'pointer' }}
  >
    <Input
      id="date"
      name="date"
      type="date"
      value={formData.date}
      onChange={handleChange}
      onKeyDown={(e) => e.preventDefault()}
      required
      className="form-input date-clickable"
      min="2026-03-14"
      style={{ cursor: 'pointer' }}
    />
  </div>
  {formData.date && (
    <small style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
      Data selectată: {formatDateForDisplay(formData.date)}
    </small>
  )}
</div>

                    <div className="form-group">
                      <label htmlFor="time" className="form-label">Ora dorită *</label>
                      {!formData.date || !gameType ? (
                        <div className="form-input" style={{ display: 'flex', alignItems: 'center', color: 'var(--text-secondary)', minHeight: '42px' }}>
                          Selectează mai întâi data și activitatea
                        </div>
                      ) : loadingTimes ? (
                        <div className="form-input" style={{ display: 'flex', alignItems: 'center', minHeight: '42px' }}>Se încarcă orele...</div>
                      ) : (
                        <>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                            gap: '6px',
                            padding: '8px',
                            background: 'rgba(0,0,0,0.25)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.1)',
                          }}>
                            {availableTimes.map((slot) => {
                              const isSelected = formData.time === slot.time;
                              const base = {
                                padding: '10px 0',
                                border: '1px solid',
                                borderRadius: '6px',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                transition: 'transform 0.05s ease',
                              };
                              const style = !slot.available
                                ? { ...base, background: 'rgba(220, 38, 38, 0.18)', borderColor: 'rgba(220, 38, 38, 0.5)', color: '#fda5a5', cursor: 'not-allowed' }
                                : isSelected
                                ? { ...base, background: '#d9fb06', borderColor: '#fff', color: '#000', cursor: 'pointer' }
                                : { ...base, background: 'rgba(34, 197, 94, 0.15)', borderColor: 'rgba(34, 197, 94, 0.5)', color: '#bbf7d0', cursor: 'pointer' };
                              return (
                                <button
                                  key={slot.time}
                                  type="button"
                                  disabled={!slot.available}
                                  onClick={() => slot.available && setFormData({ ...formData, time: slot.time })}
                                  style={style}
                                  title={slot.available ? 'Liber — click pentru a selecta' : 'Ocupat'}
                                >
                                  {slot.time}
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ display: 'flex', gap: '14px', alignItems: 'center', fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '6px', flexWrap: 'wrap' }}>
                            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: 'rgba(34, 197, 94, 0.5)', marginRight: '5px', verticalAlign: 'middle' }} />Liber</span>
                            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: 'rgba(220, 38, 38, 0.5)', marginRight: '5px', verticalAlign: 'middle' }} />Ocupat</span>
                            <span><span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: '#d9fb06', marginRight: '5px', verticalAlign: 'middle' }} />Selectat</span>
                          </div>
                          {formData.time && (
                            <small style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '6px', display: 'block' }}>
                              Ora selectată: <strong style={{ color: '#d9fb06' }}>{formData.time}</strong>
                            </small>
                          )}
                          {/* Hidden field to keep validation working */}
                          <input type="hidden" name="time" value={formData.time} required />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
  <label htmlFor="people" className="form-label">Număr persoane *</label>
  <select
    id="people"
    name="people"
    value={formData.people}
    onChange={handleChange}
    required
    className="form-input" // Păstrăm clasa pentru design consecvent
    style={{ appearance: 'none' }} // Opțional, pentru a evita stilizarea default a browserului
  >
    <option value="" disabled>Selectează numărul de persoane</option>
    <option value="2">2 persoane</option>
    <option value="3">3 persoane</option>
    <option value="4">4 persoane</option>
    <option value="5">5 persoane</option>
    <option value="6">6 persoane</option>
    <option value="7">7 persoane</option>
    <option value="8">8 persoane</option>
    <option value="9">9 persoane</option>
    <option value="10">10 persoane</option>
    <option value="11">11 persoane</option>
    <option value="12">12 persoane</option>
  </select>
</div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Mesaj adițional</label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Menționează orice detalii speciale..."
                      rows={4}
                      className="form-textarea"
                    />
                  </div>

                  <div className="form-buttons">
                    <Button
                      type="button"
                      onClick={() => submitReservation('whatsapp')}
                      disabled={submittingReservation}
                      className="btn-primary full-width"
                    >
                      <MessageCircle className="btn-icon" />
                      {submittingReservation ? 'SE TRIMITE...' : 'PREFER CONTACT PRIN WHATSAPP'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => submitReservation('email')}
                      disabled={submittingReservation}
                      className="btn-secondary full-width"
                    >
                      <Mail className="btn-icon" />
                      {submittingReservation ? 'SE TRIMITE...' : 'PREFER CONTACT PRIN EMAIL'}
                    </Button>
                  </div>
                </form>
                )}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="contact-info-wrapper">
              <Card className="contact-info-card">
                <CardHeader>
                  <CardTitle className="contact-card-title">Informații de contact</CardTitle>
                </CardHeader>
                <CardContent className="contact-info-content">
                  <a href={`tel:${mockData.contact.phone}`} className="contact-item" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
                    <Phone className="contact-icon" />
                    <div>
                      <p className="contact-label">Telefon</p>
                      <p className="contact-value">{mockData.contact.phone}</p>
                    </div>
                  </a>

                  <a href={`mailto:${mockData.contact.email}`} className="contact-item" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
                    <Mail className="contact-icon" />
                    <div>
                      <p className="contact-label">Email</p>
                      <p className="contact-value">{mockData.contact.email}</p>
                    </div>
                  </a>

                  <a href="https://maps.app.goo.gl/9W3p1zmFDNYhwXyK7?g_st=aw" target="_blank" rel="noopener noreferrer" className="contact-item" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
                    <MapPin className="contact-icon" />
                    <div>
                      <p className="contact-label">Adresă</p>
                      <p className="contact-value">{mockData.contact.address}</p>
                    </div>
                  </a>

                  <div className="contact-item">
                    <Clock className="contact-icon" style={{ color: '#d9fb06' }} />
                    <div>
                      <p className="contact-label">Program</p>
                      <p className="contact-value">
                        Luni-Vineri: {mockData.contact.schedule.weekdays}<br />
                        Sâmbătă-Duminică: {mockData.contact.schedule.weekend}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="tips-card">
                <CardHeader>
                  <CardTitle className="tips-title">Sfaturi utile</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="tips-list">
                    <li className="tip-item">Confirmăm rezervările în ordinea înscrierii.</li>
                    <li className="tip-item">Rezervă cu 48h înainte pentru siguranță.</li>
                    <li className="tip-item">Grupuri mari? Sună-ne pentru oferte!</li>
                    <li className="tip-item">Vino cu 30 de minute înainte de start.</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reservations;
